import httpClient from '@utils/httpClient';

// ============================================
// Type Definitions
// ============================================

export interface ChartDataItem {
  department?: string;
  gender?: string;
  experience_band?: string;
  age_band?: string;
  count: number;
}

export interface WorkforceAnalytics {
  departmentDistribution: ChartDataItem[];
  genderDistribution: ChartDataItem[];
  experienceDistribution: ChartDataItem[];
  ageDistribution: ChartDataItem[];
}

export interface RoleDistribution {
  role: string;
  count: number;
}

export interface HRKPIs {
  totalHeadcount: number;
  roleDistribution: RoleDistribution[];
  newHiresThisMonth: number;
  employeesOnProbation: number;
  openJobPositions: number;
  pendingLeaveRequests: number;
}

export interface AttendanceSummary {
  checkedIn: number;
  checkedOut: number;
  totalEmployees: number;
  attendanceRate: number;
}

export interface UpcomingLeave {
  id: number;
  user_id: string;
  employee_name: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  status: string;
}

export interface CompanySettings {
  company_name: string;
  company_logo_url: string | null;
  primary_color: string;
  secondary_color: string;
}

export interface Holiday {
  id: number;
  name: string;
  date: string;
  type: 'public' | 'optional' | 'restricted';
  description?: string;
}

export interface MyLeave {
  id: number;
  leave_type: string;
  start_date: string;
  end_date: string;
}

export interface Birthday {
  id: string;
  name: string;
  date_of_birth: string;
  birthday_this_year: string;
}

export interface JobOpening {
  id: number;
  title: string;
  department: string;
  description: string;
  requirements?: string;
  status: 'draft' | 'open' | 'closed' | 'on-hold';
  positions_count: number;
  location?: string;
  employment_type: string;
  salary_min?: number;
  salary_max?: number;
  posted_by: string;
  posted_by_name: string;
  created_at: string;
  updated_at: string;
  closed_at?: string;
}

export interface JobStats {
  total_jobs: number;
  open_jobs: number;
  open_positions: number;
  draft_jobs: number;
  closed_this_month: number;
  byDepartment: { department: string; count: number }[];
}

export interface Recognition {
  id: number;
  employee_id: string;
  employee_name: string;
  employee_email?: string;
  recognized_by: string;
  recognized_by_name: string;
  recognition_type: 'star_performer' | 'team_player' | 'innovation' | 'milestone' | 'customer_champion' | 'custom';
  title: string;
  description?: string;
  badge_icon: string;
  is_featured: boolean;
  created_at: string;
}

export interface Alert {
  id: number;
  alert_type: string;
  title: string;
  description?: string;
  target_role: string;
  target_user_id?: string;
  related_entity_type?: string;
  related_entity_id?: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  due_date?: string;
  action_url?: string;
  is_read: boolean;
  is_dismissed: boolean;
  created_at: string;
  read_at?: string;
  dismissed_at?: string;
}

export interface EmployeeDashboardSummary {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    department?: string;
    hire_date?: string;
  };
  attendanceStatus: {
    checkedIn: boolean;
    checkInTime?: string;
    checkOutTime?: string;
  };
  leaveBalance: {
    total: number;
    used: number;
    remaining: number;
  };
  pendingTasks: number;
  recognitionCount: number;
  upcomingHolidays: Holiday[];
}

export interface AdminDashboardSummary {
  totalUsers: number;
  usersByRole: { role: string; count: number }[];
  recentRegistrations: number;
  systemHealth: string;
}

export interface Pagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { message: string };
  pagination?: Pagination;
}

// ============================================
// Dashboard Analytics API
// ============================================

export const getWorkforceAnalytics = async (): Promise<ApiResponse<WorkforceAnalytics>> => {
  try {
    const response = await httpClient.get('/dashboard-analytics/workforce-analytics');
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch workforce analytics' }
    };
  }
};

export const getHRKPIs = async (): Promise<ApiResponse<HRKPIs>> => {
  try {
    const response = await httpClient.get('/dashboard-analytics/hr-kpis');
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch HR KPIs' }
    };
  }
};

export const getTodayAttendance = async (): Promise<ApiResponse<AttendanceSummary>> => {
  try {
    const response = await httpClient.get('/dashboard-analytics/attendance/today');
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch attendance summary' }
    };
  }
};

export const getUpcomingLeaves = async (limit = 5): Promise<ApiResponse<UpcomingLeave[]>> => {
  try {
    const response = await httpClient.get(`/dashboard-analytics/leaves/upcoming?limit=${limit}`);
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch upcoming leaves' }
    };
  }
};

export const getCompanySettings = async (): Promise<ApiResponse<CompanySettings>> => {
  try {
    const response = await httpClient.get('/dashboard-analytics/company-settings');
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch company settings' }
    };
  }
};

export const getUpcomingHolidays = async (limit = 5): Promise<ApiResponse<Holiday[]>> => {
  try {
    const response = await httpClient.get(`/dashboard-analytics/holidays/upcoming?limit=${limit}`);
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch holidays' }
    };
  }
};

export const getMyLeaves = async (month?: number, year?: number): Promise<ApiResponse<MyLeave[]>> => {
  try {
    const params = new URLSearchParams();
    if (month) params.append('month', String(month));
    if (year) params.append('year', String(year));
    const queryString = params.toString();
    const response = await httpClient.get(`/dashboard-analytics/leaves/my-calendar${queryString ? `?${queryString}` : ''}`);
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch my leaves' }
    };
  }
};

export const getUpcomingBirthdays = async (): Promise<ApiResponse<Birthday[]>> => {
  try {
    const response = await httpClient.get('/dashboard-analytics/birthdays/upcoming');
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch birthdays' }
    };
  }
};

export const getEmployeeDashboardSummary = async (): Promise<ApiResponse<EmployeeDashboardSummary>> => {
  try {
    const response = await httpClient.get('/dashboard-analytics/employee/summary');
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch dashboard summary' }
    };
  }
};

export const getAdminDashboardSummary = async (): Promise<ApiResponse<AdminDashboardSummary>> => {
  try {
    const response = await httpClient.get('/dashboard-analytics/admin/summary');
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch admin summary' }
    };
  }
};

// ============================================
// Job Openings API
// ============================================

export const getJobOpenings = async (page = 1, limit = 10, status?: string): Promise<ApiResponse<JobOpening[]> & { pagination?: Pagination }> => {
  try {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status) params.append('status', status);
    const response = await httpClient.get(`/dashboard-analytics/jobs?${params}`);
    return { success: true, data: response.jobs, pagination: response.pagination };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch job openings' }
    };
  }
};

export const getJobStats = async (): Promise<ApiResponse<JobStats>> => {
  try {
    const response = await httpClient.get('/dashboard-analytics/jobs/stats');
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch job stats' }
    };
  }
};

export const createJob = async (jobData: Partial<JobOpening>): Promise<ApiResponse<JobOpening>> => {
  try {
    const response = await httpClient.post('/dashboard-analytics/jobs', jobData);
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to create job' }
    };
  }
};

export const updateJob = async (id: number, jobData: Partial<JobOpening>): Promise<ApiResponse<JobOpening>> => {
  try {
    const response = await httpClient.put(`/dashboard-analytics/jobs/${id}`, jobData);
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to update job' }
    };
  }
};

export const deleteJob = async (id: number): Promise<ApiResponse<{ id: number; deleted: boolean }>> => {
  try {
    const response = await httpClient.delete(`/dashboard-analytics/jobs/${id}`);
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to delete job' }
    };
  }
};

// ============================================
// Recognition API
// ============================================

export const getRecognitions = async (page = 1, limit = 10, featured?: boolean): Promise<ApiResponse<Recognition[]> & { pagination?: Pagination }> => {
  try {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (featured !== undefined) params.append('featured', String(featured));
    const response = await httpClient.get(`/dashboard-analytics/recognitions?${params}`);
    return { success: true, data: response.recognitions, pagination: response.pagination };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch recognitions' }
    };
  }
};

export const getRecentRecognitions = async (limit = 5): Promise<ApiResponse<Recognition[]>> => {
  try {
    const response = await httpClient.get(`/dashboard-analytics/recognitions/recent?limit=${limit}`);
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch recognitions' }
    };
  }
};

export const getMyRecognitions = async (): Promise<ApiResponse<Recognition[]>> => {
  try {
    const response = await httpClient.get('/dashboard-analytics/recognitions/my');
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch my recognitions' }
    };
  }
};

export const createRecognition = async (data: Partial<Recognition>): Promise<ApiResponse<Recognition>> => {
  try {
    const response = await httpClient.post('/dashboard-analytics/recognitions', data);
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to create recognition' }
    };
  }
};

export const deleteRecognition = async (id: number): Promise<ApiResponse<{ id: number; deleted: boolean }>> => {
  try {
    const response = await httpClient.delete(`/dashboard-analytics/recognitions/${id}`);
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to delete recognition' }
    };
  }
};

// ============================================
// Smart Alerts API
// ============================================

export const getMyAlerts = async (page = 1, limit = 10, includeDismissed = false): Promise<ApiResponse<Alert[]> & { pagination?: Pagination }> => {
  try {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      includeDismissed: String(includeDismissed)
    });
    const response = await httpClient.get(`/dashboard-analytics/alerts?${params}`);
    return { success: true, data: response.alerts, pagination: response.pagination };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch alerts' }
    };
  }
};

export const getUnreadAlertCount = async (): Promise<ApiResponse<{ count: number }>> => {
  try {
    const response = await httpClient.get('/dashboard-analytics/alerts/unread-count');
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to fetch unread count' }
    };
  }
};

export const markAlertAsRead = async (id: number): Promise<ApiResponse<{ id: number; is_read: boolean }>> => {
  try {
    const response = await httpClient.patch(`/dashboard-analytics/alerts/${id}/read`);
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to mark alert as read' }
    };
  }
};

export const dismissAlert = async (id: number): Promise<ApiResponse<{ id: number; is_dismissed: boolean }>> => {
  try {
    const response = await httpClient.patch(`/dashboard-analytics/alerts/${id}/dismiss`);
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to dismiss alert' }
    };
  }
};

export const markAllAlertsAsRead = async (): Promise<ApiResponse<{ success: boolean }>> => {
  try {
    const response = await httpClient.post('/dashboard-analytics/alerts/mark-all-read');
    return response;
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.error?.message || 'Failed to mark all alerts as read' }
    };
  }
};
